/* eslint-disable no-nested-ternary */
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import {
  DateOverlayProps,
  ClickType,
  OVERLAY_MAX_HEIGHT,
  OVERLAY_WIDTH,
  InputPositionType,
} from './types';
import BasicIcon from '../../../BasicIcon/BasicIcon';
import Day from '../Day/Day';
import {
  DatePicker,
  DateHeader,
  Dates,
  Days,
  IconContainer,
  EmptyCell,
  PickerContainer,
  Overlay,
  MobileLabelContainer,
  MobileLabel,
  MobileLabelClose,
} from './styles';

const anchor = document.getElementById('overlay-root');

const DateOverlay: React.FC<DateOverlayProps> = (props) => {
  const {
    target,
    isOpen,
    makeDays,
    dayGrid,
    displayDate,
    momentInstance,
    singleSelection,
    inputValue,
    isMobile,
    targetHeight = 0,
    overlayPosition = 'auto',
    label,
    placeholder,
    overAll = false,
    testId,
    goBack,
    goFront,
    onSelection,
    setIsOpen,
    onClose,
  } = props;

  const updateSelection = useRef<boolean>(true);
  const divRef = useRef<HTMLDivElement>(null);
  const [parentRect, setParentRect] = useState<InputPositionType>({
    width: 0,
    top: 0,
    bottom: 0,
    x: 0,
  });
  const [selectionState, setSelectionState] = useState({
    clickState: ClickType.FirstClick,
    from: null,
    until: null,
  });

  const handleClose = useCallback(
    (event) => {
      const parent = document.getElementById(target);
      if (
        !divRef.current.contains(event.target) &&
        event.target.getAttribute('about') !== 'day' &&
        !['svg', 'path'].includes(event.target.tagName) &&
        !parent.contains(event.target)
      ) {
        if (!singleSelection && inputValue?.from && !inputValue?.until) {
          onSelection({
            from: moment(inputValue?.from, 'DD/MM/YYYY').format('DD/MM/YYYY'),
            until: moment(inputValue?.from, 'DD/MM/YYYY').format('DD/MM/YYYY'),
          });
        }
        setIsOpen(false);
      }
    },
    [singleSelection, inputValue, setIsOpen, onSelection, target],
  );
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClose);
    } else {
      document.removeEventListener('mousedown', handleClose);
    }
    return () => document.removeEventListener('mousedown', handleClose);
  }, [handleClose, isOpen]);

  useEffect(() => {
    const parent = document.getElementById(target);
    if (parent) {
      const newRect: DOMRect = parent.getBoundingClientRect();
      setParentRect({
        width: newRect?.width,
        top: newRect?.top + window.scrollY,
        bottom: newRect?.bottom + window.scrollY,
        x: newRect?.x,
      });
    }
  }, [target, isOpen]);

  useEffect(() => {
    if (!updateSelection.current) {
      updateSelection.current = true;
      return;
    }

    setSelectionState({
      clickState:
        !inputValue?.from && !inputValue?.until
          ? ClickType.FirstClick
          : !!inputValue?.from && !inputValue?.until
          ? ClickType.SecondClick
          : ClickType.OtherClick,
      from: singleSelection
        ? moment(inputValue, 'DD/MM/YYYY')
        : inputValue?.from
        ? moment(inputValue?.from, 'DD/MM/YYYY')
        : null,
      until: singleSelection
        ? moment(inputValue, 'DD/MM/YYYY')
        : inputValue?.until
        ? moment(inputValue?.until, 'DD/MM/YYYY')
        : null,
    });
  }, [inputValue, singleSelection]);

  const dayClick = useCallback(
    (day: number) => {
      const actualDay = momentInstance
        .clone()
        .startOf('month')
        .add(day - 1, 'days');

      if (singleSelection) {
        onSelection(actualDay.format('DD/MM/YYYY'));
      } else if (selectionState.clickState === ClickType.FirstClick) {
        setSelectionState({
          ...selectionState,
          from: actualDay,
          until: actualDay,
          clickState: ClickType.SecondClick,
        });
        onSelection({
          from: actualDay.format('DD/MM/YYYY'),
          until: '',
        });
      } else if (selectionState.clickState === ClickType.SecondClick) {
        const from = moment(inputValue?.from, 'DD/MM/YYYY');
        if (actualDay.isBefore(from)) {
          setSelectionState({
            ...selectionState,
            from: actualDay,
            until: actualDay,
          });
          onSelection({
            from: actualDay.format('DD/MM/YYYY'),
            until: '',
          });
        } else {
          onSelection({
            from: selectionState.from?.format('DD/MM/YYYY'),
            until: selectionState.until
              ? selectionState.until?.format('DD/MM/YYYY')
              : selectionState.from?.format('DD/MM/YYYY'),
          });
          setSelectionState({
            ...selectionState,
            clickState: ClickType.OtherClick,
          });
        }
      } else if (selectionState.clickState === ClickType.OtherClick) {
        const from = moment(inputValue?.from, 'DD/MM/YYYY');
        const until = moment(inputValue?.until, 'DD/MM/YYYY');
        if (actualDay.isSame(from)) {
          setSelectionState({
            ...selectionState,
            from: until,
            until,
            clickState: ClickType.SecondClick,
          });
          onSelection({
            from: until.format('DD/MM/YYYY'),
            until: '',
          });
        } else if (actualDay.isSame(until)) {
          updateSelection.current = false;
          setSelectionState({
            ...selectionState,
            clickState: ClickType.SecondClick,
          });
          onSelection({
            from: from.format('DD/MM/YYYY'),
            until: '',
          });
        } else if (actualDay.isAfter(from)) {
          setSelectionState({
            ...selectionState,
            until: actualDay,
          });
          onSelection({
            from: inputValue?.from,
            until: moment(actualDay, 'DD/MM/YYYY').format('DD/MM/YYYY'),
          });
        } else if (actualDay.isBefore(from)) {
          setSelectionState({
            ...selectionState,
            from: actualDay,
            until: actualDay,
            clickState: ClickType.SecondClick,
          });
          onSelection({
            from: actualDay.format('DD/MM/YYYY'),
            until: '',
          });
        }
      }
    },
    [selectionState, momentInstance, onSelection, singleSelection, inputValue],
  );

  const onHover = useCallback(
    (day: number) => {
      const actualMonth = momentInstance.clone();
      const hoverDay = actualMonth
        .clone()
        .startOf('month')
        .add(day - 1, 'days');
      const isAfter = selectionState.from && hoverDay.isAfter(selectionState.from);
      const isSameFrom =
        selectionState.from &&
        selectionState.from.isSame(
          moment()
            .startOf('month')
            .add(day - 1, 'days'),
        );
      if (selectionState.clickState === ClickType.SecondClick && (isAfter || isSameFrom)) {
        setSelectionState({
          ...selectionState,
          until: hoverDay,
        });
      }
    },
    [selectionState, momentInstance],
  );

  const position = useMemo(() => {
    const html = document.documentElement;
    const height = Math.max(html.clientHeight, html.scrollHeight);
    const bottomSpace = height - parentRect?.bottom;
    const upperSpace = parentRect?.top;
    const autoPosition =
      bottomSpace > OVERLAY_MAX_HEIGHT || bottomSpace > upperSpace
        ? parentRect?.top + targetHeight + 6
        : parentRect?.top - OVERLAY_MAX_HEIGHT - 6;
    const top =
      overlayPosition === 'top'
        ? parentRect?.top - OVERLAY_MAX_HEIGHT - 6
        : overlayPosition === 'bottom'
        ? parentRect?.top + targetHeight + 6
        : autoPosition;

    const left =
      parentRect.width >= OVERLAY_WIDTH
        ? parentRect?.x
        : parentRect?.x + parentRect.width - OVERLAY_WIDTH;

    return {
      top,
      left,
    };
  }, [parentRect, targetHeight, overlayPosition]);

  return (
    <DatePicker
      ref={divRef}
      isVisible={isOpen}
      left={position.left}
      top={position.top}
      width={parentRect?.width}
      data-testid={`${testId}-overlay`}
      isMobile={isMobile}
      overAll={overAll}
    >
      {isMobile && (
        <MobileLabelContainer>
          <MobileLabel>{label || placeholder || 'Seleccionar'}</MobileLabel>
          <MobileLabelClose onClick={onClose}>
            <BasicIcon name="x" />
          </MobileLabelClose>
        </MobileLabelContainer>
      )}
      <PickerContainer isMobile={isMobile}>
        <DateHeader>
          <IconContainer role="presentation" onClick={goBack}>
            <BasicIcon size="large" name="chevron-left" />
          </IconContainer>
          <span>{displayDate}</span>
          <IconContainer role="presentation" onClick={goFront}>
            <BasicIcon size="large" name="chevron-right" />
          </IconContainer>
        </DateHeader>
        <Days>
          {makeDays.map((day) => (
            <span key={day}>{day}</span>
          ))}
        </Days>
        <Dates>
          {dayGrid.map((day) => {
            const actualDay = momentInstance
              .clone()
              .startOf('month')
              .add(day.value - 1, 'days');
            return day.value === 0 ? (
              <EmptyCell key={day.id} />
            ) : (
              <Day
                key={day.id}
                isFrom={selectionState.from && selectionState.from.isSame(actualDay)}
                isFromInterval={actualDay.isBetween(
                  selectionState.from,
                  selectionState.until,
                  'day',
                  '()',
                )}
                isTo={selectionState.until && selectionState.until.isSame(actualDay)}
                isEqual={
                  selectionState.from &&
                  selectionState.until &&
                  selectionState.from.isSame(actualDay) &&
                  selectionState.until.isSame(actualDay)
                }
                selectionActive={selectionState.clickState === ClickType.SecondClick}
                day={day.value}
                onDayClick={dayClick}
                onHoverDay={onHover}
                isAvailable={day.available}
                isWeekend={day.weekend}
                type={day.type}
              />
            );
          })}
        </Dates>
      </PickerContainer>
    </DatePicker>
  );
};

const DateOverlayMobile: React.FC<DateOverlayProps> = (props) => {
  const { isOpen, isMobile, overAll } = props;
  const [element] = useState(document.createElement('div'));

  useEffect(() => {
    element.style.zIndex = '99999';
    if (anchor) {
      anchor.appendChild(element);
    }

    return () => {
      anchor.removeChild(element);
    };
  }, [element]);

  return isMobile
    ? createPortal(
        <Overlay isVisible={isOpen} overAll={overAll}>
        <DateOverlay {...props} />
      </Overlay>,
        element,
      )
    : createPortal(<DateOverlay {...props} />, element);
};

export default DateOverlayMobile;
